import { colorSet } from '@components/Provider';
import { makeStyles, Theme } from '@material-ui/core';
import {
  CreateCSSProperties,
  CSSProperties
} from '@material-ui/core/styles/withStyles';
import { MapDispatchToPropsFunction } from 'react-redux';

type StyleSheetReturnType =
  | CSSProperties
  | CreateCSSProperties<{}>
  | MapDispatchToPropsFunction<{}, CreateCSSProperties<{}>>;

const useStyles = makeStyles((theme) => ({
  'rfq-container': {
    width: '100%',
    minHeight: '100vh',

    '& .errorMessage': {
      display: 'block',
      padding: '0px 3px',
      marginTop: 8,
      ...theme.typography.caption,
      color: colorSet.errorMain
    },

    '& .budget': {
      position: 'absolute',
      bottom: -18,
      '&.cost': {
        display: 'block',
        padding: '0px 3px',
        width: '100%',
        textAlign: 'right',
        ...theme.typography.caption,
        color: colorSet.gray600
      }
    },

    [theme.breakpoints.between(375, 961)]: {
      paddingRight: 24,
      paddingLeft: 24
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: 16,
      paddingLeft: 16
    }
  },

  'option-select': {
    ...theme.typography.body2,
    fontWeight: 'normal',
    padding: '10px',

    '& .option-menu-item': {
      ...theme.typography.body2,
      fontWeight: 'normal',
      '&.Mui-selected': {
        backgroundColor: colorSet.gray000
      },
      '&.Mui-focusVisible': {
        backgroundColor: 'rgba(227, 242, 253, 1) !important'
      },
      '&:hover': {
        backgroundColor: 'rgba(227, 242, 253, 0.5) !important'
      }
    }
  },

  'rfq-info-section': {
    marginBottom: 106,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 126
    },
    '& .title': {
      wordBreak: 'break-all',
      ...theme.typography.h2,
      fontWeight: 'bold',
      [theme.breakpoints.down(376)]: {
        ...theme.typography.h3,
        fontWeight: 'bold'
      }
    },

    '&.rfq-header': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      marginTop: 72,
      marginBottom: 40,
      '& .rfq-header-servicetype-chip': {
        padding: '8px 24px',
        height: 'auto',
        borderRadius: 64,
        backgroundColor: '#f5f5f5',
        marginBottom: 40,
        '& .MuiChip-label': {
          paddingLeft: 0,
          paddingRight: 0
        },
        '& .MuiDivider-root': {
          backgroundColor: colorSet.gray600,
          marginRight: 8,
          marginLeft: 8,
          height: 16
        },
        '& .MuiGrid-item': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 40,
          padding: '10px 16px',
          '& svg': {
            marginRight: 8,
            width: 24,
            height: 24
          }
        }
      },

      '& .rfq-header-guide-tooltip': {
        backgroundColor: colorSet.primary500
      },
      '& .rfq-header-guide-chip': {
        width: 177,
        height: 32,
        backgroundColor: colorSet.gray200
      },
      '& .rfq-header-tooltip': {
        marginTop: 8,
        '& .startIcon': {
          color: '#dadada',
          width: 24,
          height: 24
        }
      }
    },

    '&.rfq-upload': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',

      '& .empty-box': {
        maxWidth: 912,
        width: '100%',
        border: `1px solid ${colorSet.gray400}`,
        borderRadius: 4,
        padding: 16,
        '&.error': {
          border: `1px solid ${colorSet.errorMain}`
        }
      }
    }
  },
  'rfq-dropzone': {
    ...dropzoneStyles
  },
  'rfq-drawingfiles-table': {
    ...tableStyles
  },
  'rfq-drawingfiles-card': {
    ...cardStyles
  },
  'rfq-security-setting': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...securitySettingStyles(theme)
  },
  'rfq-consultationMethod-setting': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...consultMethodSettingStyles(theme)
  },
  'rfq-option-select-dialog': {
    ...optionSelectDialogStyles(theme)
  },
  'rfq-option-recommendation-box': {
    flexWrap: 'wrap',
    ...optionRecommendationBoxStyles(theme)
  },
  'rfq-ref': {
    ...rfqRefStyles(theme)
  },
  'rfq-project-info': {
    ...rfqProjectInfoStyles(theme)
  },
  'rfq-submit': {
    ...rfqSubmitStyles(theme)
  },
  'rfq-sheetMetal': {
    ...rfqSheetMetalStyles(theme)
  },
  'printing-tooltip': {
    marginTop: 12,
    maxWidth: 912,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    '& .tooltip-text': {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: 4
      }
    }
  },
  'tooltip-paper': {
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.1), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',
    padding: 20,
    backgroundColor: colorSet.gray000,
    color: colorSet.gray900
  }
}));

const dropzoneStyles: StyleSheetReturnType = {
  backgroundColor: colorSet.primary200,
  border: `1px dashed ${colorSet.primary500}`,
  maxWidth: 912,
  width: '100%',
  maxHeight: 160,
  height: '100%',
  marginTop: 8,
  padding: 8,
  minHeight: 119,
  transition: 'all 0.2s ease-in-out',
  '&.empty': {
    padding: 28,
    minHeight: 159
  },
  '&.inprogress': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '& #file-dropzone': {
    display: 'none'
  },
  '& .rfq-dropzone-text': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 4,
    '& span': {
      fontWeight: 'normal',
      fontSize: 14,
      color: colorSet.gray900
    }
  },

  '& .rfq-dropzone-button-group': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 12,
    '& > :nth-child(2)': {
      margin: '0px 8px'
    },
    '& button': {
      '&:hover': {
        textDecoration: 'none'
      }
    }
  },

  '& .rfq-dropzone-accept-files': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
  }
};

const cardStyles: StyleSheetReturnType = {
  '& .card': {
    '& .card-header': {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      backgroundColor: colorSet.gray200,
      '& .index': { marginRight: 8 },
      '& .fileName': {
        marginRight: 8
      },
      '& .delete-button': {
        position: 'absolute',
        color: colorSet.gray2000,
        marginLeft: 24,
        right: 8
      }
    },
    '& .card-body': {
      padding: '24px 16px',
      justifyContent: 'flex-start'
    },
    '& .card-data': {
      display: 'flex',
      marginBottom: 16,
      '& .data-title': {
        width: 60,
        marginRight: 8
      },
      '& .data-options': {
        width: '100%'
      }
    }
  }
};

const tableStyles: StyleSheetReturnType = {
  maxWidth: 912,
  width: '100%',
  '& .option-all': {
    position: 'relative',
    height: 32,
    '& > button': {
      position: 'absolute',
      right: 0,
      textDecoration: 'none'
    }
  },
  '& .table-header': {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12.5px',
    backgroundColor: colorSet.gray300,
    '& .table-header-item': {
      width: '100%',
      '& > span': {
        fontSize: 14,
        fontWeight: 'normal',
        lineHeight: '20px',
        color: colorSet.gray900
      }
    }
  },
  '& .table-body': {},
  '& .table-data': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 16px',
    minHeight: 80,
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& .item-group': {
      width: '100%',
      display: 'flex'
    },
    '& .button-group': {
      display: 'flex',
      alignItems: 'center',
      '& .option-button': {
        width: 120,
        '& > span': {
          fontSize: 14,
          fontWeight: 400
        }
      },

      '& .delete-button': {
        color: colorSet.gray2000,
        marginLeft: 24
      }
    }
  }
};

const securitySettingStyles = (theme: Theme): StyleSheetReturnType => {
  return {
    '& .tooltip-title-box': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      marginBottom: 40,
      '& .tooltip': {
        height: 24,
        marginLeft: 16,
        '& > svg': {
          color: '#dadada'
        }
      }
    },
    '& .radioGroup': {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      width: '100%',
      height: 182,
      maxWidth: 912,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        '& > :first-child': {
          marginBottom: 12
        }
      },
      '& .MuiIconButton-root': {
        borderRadius: '0'
      }
    },
    '& .radioBox': {
      display: 'flex',
      flexDirection: 'column',
      height: 182,
      width: 'calc(100% / 2 - 6px)',
      marginLeft: 0,
      marginRight: 0,
      borderRadius: 4,
      border: `2px solid ${colorSet.gray400}`,
      padding: 16,
      '&.selected': {
        border: `2px solid ${colorSet.primary500}`,
        backgroundColor: colorSet.primary100,
        transition: 'all 0.2s ease-in-out'
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '100%'
      }
    },
    '& .radioInnerBox': {
      '& .radio-label-box': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        '& svg': {
          marginRight: 8
        },
        '& .radio-label': {
          ...theme.typography.h6,
          fontWeight: 'bold',
          [theme.breakpoints.down('xs')]: {
            ...theme.typography.body1,
            fontWeight: 'bold'
          }
        }
      },
      '& .radio-label-list': {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8
      }
    }
  };
};

const consultMethodSettingStyles = (theme: Theme): StyleSheetReturnType => {
  return {
    '& .tooltip-title-box': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      marginBottom: 40,
      '& .tooltip': {
        height: 24,
        marginLeft: 16,
        '& > svg': {
          color: '#dadada'
        }
      }
    },
    '& .radioGroup': {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      width: '100%',
      height: 182,
      maxWidth: 912,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        '& > :first-child': {
          marginBottom: 12
        }
      },
      '& .MuiIconButton-root': {
        borderRadius: '0'
      }
    },
    '& .radioBox': {
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
      height: 202,
      width: 'calc(100% / 2 - 6px)',
      marginLeft: 0,
      marginRight: 0,
      borderRadius: 4,
      border: `2px solid ${colorSet.gray400}`,
      padding: 16,
      '&.selected': {
        border: `2px solid ${colorSet.primary500}`,
        backgroundColor: colorSet.primary100,
        transition: 'all 0.2s ease-in-out'
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '100%'
      }
    },
    '& .radioInnerBox': {
      '& .radio-label-box': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        '& svg': {
          marginRight: 8
        },
        '& .radio-label': {
          ...theme.typography.h6,
          fontWeight: 'bold',
          [theme.breakpoints.down('xs')]: {
            ...theme.typography.body1,
            fontWeight: 'bold'
          }
        }
      },
      '& .radio-label-list': {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8
      }
    }
  };
};

const optionSelectDialogStyles = (theme: Theme): StyleSheetReturnType => {
  return {
    width: '100%',
    '& .option-field': {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 20,
      '&.top': {
        marginBottom: 24,
        '& .fileName': {
          padding: '12px 24px',
          backgroundColor: colorSet.gray100,
          borderRadius: 4,
          border: `1px solid ${colorSet.gray400}`
        }
      },
      '&.bottom': {
        marginBottom: 0
      }
    },
    '& .button-group': {
      display: 'flex',
      '& > :first-child': {
        marginRight: 12
      }
    },

    '& .errorMessage': {
      display: 'block',
      padding: '0px 3px',
      ...theme.typography.caption,
      color: colorSet.errorMain
    }
  };
};

const optionRecommendationBoxStyles = (theme: Theme): StyleSheetReturnType => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorSet.gray100,
    padding: '12px 14px',
    borderRadius: 8,
    marginBottom: 8,
    '& .recommend-chip': {
      width: 'fit-content',
      backgroundColor: colorSet.gray000,
      padding: '4px 10px',
      border: `1px solid ${colorSet.gray200}`,
      borderRadius: 40,
      margin: '4px 2px',
      cursor: 'pointer',
      '&.active': {
        backgroundColor: colorSet.primary500
      },
      '&.disabled': {
        opacity: 0.4,
        cursor: 'initial'
      }
    }
  };
};

const rfqRefStyles = (theme: Theme): StyleSheetReturnType => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  '& #ref-upload-input': {
    display: 'none'
  },
  '& .rfq-header-guide-chip': {
    width: 177,
    height: 32,
    backgroundColor: colorSet.gray200
  },
  '& .ref-field': {
    width: '100%',
    maxWidth: 912,
    '&.detailedRequest': {
      marginTop: 40,
      marginBottom: 64
    },
    '&.ref-files': {
      position: 'relative',
      '& .total': {
        ...theme.typography.body2,
        color: colorSet.gray900,
        marginLeft: 12
      },
      '& .ref-upload-button': {
        position: 'absolute',
        right: 0,
        top: 0
      },
      '& .ref-files-list': {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: 656,
        '& > :nth-child(2n-1)': {
          marginRight: 16,
          [theme.breakpoints.down('xs')]: {
            marginRight: 0
          }
        },
        '& .ref-file': {
          display: 'flex',
          alignItems: 'center',
          maxWidth: 320,
          width: '100%',
          padding: '8px 11px',
          '& > :nth-child(2)': {
            margin: '0px 12px'
          },
          '& > button': {
            padding: 0
          }
        }
      }
    }
  }
});

const rfqProjectInfoStyles = (theme: Theme): StyleSheetReturnType => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  '& .info-field': {
    width: '100%',
    maxWidth: 912,
    '&.gutter-bottom': {
      marginBottom: 64
    },
    '& .project-purpose': {
      flexWrap: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        flexWrap: 'wrap'
      },
      '& .purpose-select-input': {
        marginRight: 8,
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
          marginBottom: 8
        }
      }
    },
    '& .sub-checkbox': {
      marginRight: 0,
      marginLeft: 0,
      marginTop: 12,
      '& .MuiFormControlLabel-label': {
        ...theme.typography.body2,
        color: colorSet.gray900
      }
    },
    '& .budget-input': {
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    '& .text': {
      marginRight: 8
    }
  }
});

const rfqSubmitStyles = (theme: Theme): StyleSheetReturnType => ({
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 120,
  '& > :first-child': {
    marginRight: 12
  }
});

const rfqSheetMetalStyles = (theme: Theme): StyleSheetReturnType => ({
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  '& .extension': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: 24
  },

  '& .primaryText': {
    ...theme.typography.h4,
    fontWeight: 700,
    textAlign: 'center',
    marginTop: 12,
    marginBottom: 8
  },

  '& .extensionRoot': {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '16px',
    maxWidth: 912,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },

  '& .extensionBox': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 0,
    marginRight: 0,
    width: 'calc(100% / 2 - 6px)',
    height: '100%',
    border: `2px solid ${colorSet.gray400}`,
    borderRadius: 4,
    [theme.breakpoints.down('xs')]: {
      width: 'calc(100%)',
      marginBottom: 16
    },
    '& .FormControlLabel': {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    '&:hover': {
      border: `2px solid ${colorSet.primary500}`
    },

    '&.checked': {
      border: `2px solid ${colorSet.primary500}`
    }
  },
  '& .relatedServiceImage': {
    marginTop: 16,
    width: '100%',
    height: '100%',
    maxWidth: '100%'
  }
});
export default useStyles;
